import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  closeEditModal,
  setEditDraftText,
  generateAiReply,
  saveDraft,
  publishReply,
} from '../../store/slices/googleReviewSlice';
import { dateFormatted } from '../../helpers/dateFormatted';

const MAX_CHARS = 4096;

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <span className="edit-reply-modal__stars" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? 'review-card__star--filled' : 'review-card__star--empty'}>
        ★
      </span>
    ))}
  </span>
);

const EditReplyModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { currentEditReview, currentEditDraftText, isGenerating, isPublishing } =
    useAppSelector((s) => s.googleReview);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (currentEditReview) {
      textareaRef.current?.focus();
    }
  }, [currentEditReview]);

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch(closeEditModal());
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dispatch]);

  if (!currentEditReview || !currentEditReview.reviewId) return null;

  const review = currentEditReview;
  const reviewId = currentEditReview.reviewId;
  const charCount = currentEditDraftText.length;
  const isOverLimit = charCount > MAX_CHARS;
  const canSubmit = charCount > 0 && !isOverLimit && !isGenerating && !isPublishing;

  const handleGenerate = () => {
    dispatch(generateAiReply(reviewId));
  };

  const handleSaveDraft = () => {
    if (!canSubmit) return;
    dispatch(saveDraft({ reviewId, text: currentEditDraftText }));
  };

  const handlePublish = () => {
    if (!canSubmit) return;
    dispatch(publishReply({ reviewId, text: currentEditDraftText }));
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="edit-reply-modal__backdrop"
        onClick={() => dispatch(closeEditModal())}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="edit-reply-modal" role="dialog" aria-modal="true" aria-label={t('google_review_modal_title')}>

        {/* Header */}
        <div className="edit-reply-modal__header">
          <h2 className="edit-reply-modal__title">{t('google_review_modal_title')}</h2>
          <button
            className="edit-reply-modal__close"
            onClick={() => dispatch(closeEditModal())}
            aria-label={t('google_review_modal_close')}
          >
            ✕
          </button>
        </div>

        {/* Review details */}
        <div className="edit-reply-modal__review">
          <div className="edit-reply-modal__review-header">
            <div className="review-card__avatar" aria-hidden="true">
              {review.reviewerAvatarInitials}
            </div>
            <div className="review-card__meta">
              <span className="review-card__name">{review.reviewerName}</span>
              <span className="review-card__date">{dateFormatted(review.createdAt ?? '')}</span>
            </div>
          </div>
          <StarRating rating={review.rating ?? 0} />
          <p className="edit-reply-modal__review-text">{review.text}</p>
        </div>

        <div className="edit-reply-modal__divider" />

        {/* Reply textarea */}
        <div className="edit-reply-modal__reply-section">
          <div className="edit-reply-modal__textarea-header">
            <span className="edit-reply-modal__reply-label">{t('google_review_modal_reply_label')}</span>
            <button
              className="edit-reply-modal__generate-btn"
              onClick={handleGenerate}
              disabled={isGenerating || isPublishing}
            >
              {isGenerating ? (
                <>
                  <span className="edit-reply-modal__spinner" aria-hidden="true" />
                  {t('google_review_modal_generating')}
                </>
              ) : (
                <>✨ {t('google_review_modal_generate')}</>
              )}
            </button>
          </div>

          <textarea
            ref={textareaRef}
            className={`edit-reply-modal__textarea${isOverLimit ? ' edit-reply-modal__textarea--over-limit' : ''}`}
            value={currentEditDraftText}
            onChange={(e) => dispatch(setEditDraftText(e.target.value))}
            placeholder={t('google_review_modal_placeholder')}
            rows={7}
            disabled={isGenerating || isPublishing}
          />

          <div className="edit-reply-modal__char-counter">
            <span className={isOverLimit ? 'edit-reply-modal__char-counter--over' : ''}>
              {charCount} / {MAX_CHARS}
            </span>
          </div>
        </div>

        {/* Footer actions */}
        <div className="edit-reply-modal__footer">
          <button
            className="btn btn-secondary edit-reply-modal__draft-btn"
            onClick={handleSaveDraft}
            disabled={!canSubmit}
          >
            {t('google_review_modal_save_draft')}
          </button>
          <button
            className="btn btn-primary edit-reply-modal__publish-btn"
            onClick={handlePublish}
            disabled={!canSubmit || isPublishing}
          >
            {isPublishing ? (
              <>
                <span className="edit-reply-modal__spinner edit-reply-modal__spinner--white" aria-hidden="true" />
                {t('google_review_modal_publishing')}
              </>
            ) : (
              t('google_review_modal_publish')
            )}
          </button>
        </div>

      </div>
    </>
  );
};

export default EditReplyModal;
